import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import { Printer, UploadSimple, PaperPlaneTilt, DownloadSimple } from "@phosphor-icons/react";

const STATUS_TINT = {
  delivered: "text-emerald-600", received: "text-emerald-600", queued: "text-amber-600", "media.processed": "text-amber-600",
  "sending.started": "text-amber-600", sending: "text-amber-600", failed: "text-red-500",
};

export default function Fax() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["faxes"], queryFn: async () => (await api.get("/fax/list")).data, refetchInterval: 12000 });
  const faxes = data?.faxes || [];
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim() || !file) { toast.error("Enter a fax number and choose a PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("file", file);
      fd.append("subject", subject.trim());
      fd.append("note", note.trim());
      await api.post("/fax/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fax queued with a branded cover sheet — watch the status below");
      setFile(null); setTo(""); setSubject(""); setNote("");
      qc.invalidateQueries({ queryKey: ["faxes"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Cloud Fax</h1>
        <p className="mt-2 text-muted-foreground">Send a PDF to any fax number (e.g. the IRS) from your number. Delivered securely.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Destination fax number (E.164)</label>
            <input data-testid="fax-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+18008291040"
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PDF document</label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-input bg-card px-3 py-2.5 text-sm text-muted-foreground hover:border-primary">
              <UploadSimple size={16} /> {file ? file.name : "Choose a PDF…"}
              <input data-testid="fax-file" type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject (on cover sheet)</label>
            <input data-testid="fax-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Form 2553 — S Corp Election"
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message (optional)</label>
            <input data-testid="fax-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note printed on the cover page"
              className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <button data-testid="fax-send" onClick={send} disabled={busy}
          className="mt-4 flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
          <PaperPlaneTilt size={18} weight="bold" /> {busy ? "Sending…" : "Send fax"}
        </button>
        <p className="mt-2 text-xs text-muted-foreground">Tip: confirm the exact IRS fax number and include any required cover sheet in your PDF. A branded iYeneh Connect cover page is added automatically. Max 20 MB.</p>
      </div>

      <div>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Faxes</h2>
        <div className="space-y-2">
          {faxes.length === 0 && <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">No faxes yet. Sent and received faxes will appear here.</div>}
          {faxes.map((f) => {
            const inbound = f.direction === "inbound";
            return (
            <div key={f.id} data-testid={`fax-${f.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-accent ${inbound ? "text-emerald-600" : "text-primary"}`}>
                  {inbound ? <DownloadSimple size={18} weight="duotone" /> : <Printer size={18} weight="duotone" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono-metric text-sm font-medium">{inbound ? (f.from || "Unknown") : f.to}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${inbound ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary"}`}>{inbound ? "Received" : "Sent"}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {inbound ? `${f.pages ? f.pages + " page(s) · " : ""}${shortDate(f.created_at)}` : `${f.filename} · ${shortDate(f.created_at)}`}
                    {inbound && f.media_url && <> · <a data-testid={`fax-download-${f.id}`} href={f.media_url} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">Download</a></>}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-semibold capitalize ${STATUS_TINT[f.status] || "text-muted-foreground"}`}>{(f.status || "queued").replace(".", " ")}</p>
                {f.failure_reason && <p className="text-xs text-red-500">{f.failure_reason}</p>}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
